import { useMemo } from 'react';
import { scaleLinear } from '@visx/scale';
import { useTheme } from '@simplywallst/ui-core';
import { max, stop, getSegments, getRingRadius } from '@components/Gauge/utils';
import {
  stop as stopFV,
  getRingRadius as getRadiusFV,
  stopOverflow,
} from '@/components/GaugeFairValue/utils';
import { Arc } from '@components/Gauge';
import type { DEFINITION_URLS } from './constants';

const definitionId: Record<keyof typeof DEFINITION_URLS, string> = {
  gaugeBody: 'gauge-assets-body',
  gaugeFairValueBody: 'gauge-fair-value-body',
  gaugeFairValueFillBad: 'gauge-fair-value-fill-bad',
  gaugeFairValueFillGood: 'gauge-fair-value-fill-good',
  gaugeFairValueRightOverflow: 'gauge-fair-value-right-overflow',
  gaugeFairValueLeftOverflow: 'gauge-fair-value-left-overflow',
  gradientId: 'gauge-assets-gradient',
  xLargePin: 'gauge-assets-xLarge-pin',
  largePin: 'gauge-assets-large-pin',
  mediumPin: 'gauge-assets-medium-pin',
  smallPin: 'gauge-assets-small-pin',
  actualBackgroundGradient: 'ActualBackgroundGradient',
  yearMarkerGradient: 'YearMarkerGradient',
  chartFailPattern: 'Chart_Fail_Pattern',
  chartFailPatternInverse: 'Chart_Fail_Pattern_Inverse',
  noirGradient01: 'Noir_Gradient_01',
  noirGradient02: 'Noir_Gradient_02',
  chart01Gradient01: 'Chart_01_Gradient_01',
  chart01Gradient02: 'Chart_01_Gradient_02',
  chart02Gradient01: 'Chart_02_Gradient_01',
  chart02Gradient02: 'Chart_02_Gradient_02',
  chart03Gradient01: 'Chart_03_Gradient_01',
  chart03Gradient02: 'Chart_03_Gradient_02',
  chart04Gradient01: 'Chart_04_Gradient_01',
  chart04Gradient02: 'Chart_04_Gradient_02',
  chart05Gradient01: 'Chart_05_Gradient_01',
  chart05Gradient02: 'Chart_05_Gradient_02',
  chart06Gradient01: 'Chart_06_Gradient_01',
  chart06Gradient02: 'Chart_06_Gradient_02',
  chartNEGGradient01: 'Chart_NEG_Gradient_01',
  chartNEGGradient02: 'Chart_NEG_Gradient_02',
  chartPOSGradient02: 'Chart_POS_Gradient_02',
} as const;

export function Definitions() {
  const segments = useMemo(() => getSegments(), []);
  const theme = useTheme();

  const spectrumColors = [
    theme.color.bad,
    theme.color.neutral,
    theme.color.good,
  ];

  const spectrumScale = useMemo(
    () =>
      scaleLinear({
        domain: [-max, 0, max],
        range: spectrumColors,
      }),
    []
  );
  return (
    <svg
      style={{
        display: 'block',
        width: 0,
        height: 0,
      }}
    >
      <defs>
        {segments.map((n, i, a) => {
          if (a[i + 1] === undefined) return null;
          return (
            <linearGradient
              key={`${definitionId.gradientId}-${i}`}
              id={`${definitionId.gradientId}-${i}`}
            >
              <stop stopColor={spectrumScale(n)} />
              <stop stopColor={spectrumScale(a[i + 1])} />
            </linearGradient>
          );
        })}
        <g id={definitionId.gaugeFairValueBody}>
          <Arc
            outerRadius={getRadiusFV('outer')}
            innerRadius={getRadiusFV('inner')}
            startAngle={0}
            endAngle={-stopFV}
            fill={spectrumColors[0]}
          />
          <Arc
            outerRadius={getRadiusFV('outer')}
            innerRadius={getRadiusFV('inner')}
            startAngle={0}
            endAngle={stopFV}
            fill={spectrumColors[spectrumColors.length - 1]}
          />
        </g>
        <g id={definitionId.gaugeFairValueFillBad}>
          <Arc
            outerRadius={
              getRadiusFV('outer') -
              (getRadiusFV('outer') - getRadiusFV('inner'))
            }
            innerRadius={12}
            startAngle={0}
            endAngle={-stopFV}
          />
        </g>
        <g id={definitionId.gaugeFairValueFillGood}>
          <Arc
            outerRadius={
              getRadiusFV('outer') -
              (getRadiusFV('outer') - getRadiusFV('inner'))
            }
            innerRadius={12}
            startAngle={0}
            endAngle={stopFV}
            fill={spectrumColors[spectrumColors.length - 1]}
            fillOpacity={0.2}
          />
        </g>
        <g id={definitionId.gaugeFairValueRightOverflow}>
          <Arc
            outerRadius={getRadiusFV('outer')}
            innerRadius={getRadiusFV('inner')}
            startAngle={0}
            endAngle={-stopOverflow}
            fill={spectrumColors[0]}
            fillOpacity={0.3}
          />
          <Arc
            outerRadius={
              getRadiusFV('outer') -
              (getRadiusFV('outer') - getRadiusFV('inner'))
            }
            innerRadius={12}
            startAngle={0}
            endAngle={-stopOverflow}
            fill={spectrumColors[0]}
            fillOpacity={0.2}
          />
        </g>
        <g id={definitionId.gaugeFairValueLeftOverflow}>
          <Arc
            outerRadius={getRadiusFV('outer')}
            innerRadius={getRadiusFV('inner')}
            startAngle={0}
            endAngle={stopOverflow}
            fill={spectrumColors[spectrumColors.length - 1]}
            fillOpacity={0.3}
          />
          <Arc
            outerRadius={
              getRadiusFV('outer') -
              (getRadiusFV('outer') - getRadiusFV('inner'))
            }
            innerRadius={12}
            startAngle={0}
            endAngle={stopOverflow}
            fill={spectrumColors[spectrumColors.length - 1]}
            fillOpacity={0.2}
          />
        </g>
        <g id={definitionId.gaugeBody}>
          <Arc
            outerRadius={getRingRadius('outer')}
            innerRadius={getRingRadius('inner')}
            startAngle={0}
            endAngle={-stop}
            fill={spectrumColors[0]}
            fillOpacity={0.5}
          />
          <Arc
            outerRadius={getRingRadius('outer')}
            innerRadius={getRingRadius('inner')}
            startAngle={0}
            endAngle={stop}
            fill={spectrumColors[spectrumColors.length - 1]}
            fillOpacity={0.5}
          />
          {segments.map((n, i, a) => {
            if (a[i + 1] === undefined) return null;
            return (
              <Arc
                key={`gauge-arc-linearGradient${i}`}
                outerRadius={getRingRadius('outer')}
                innerRadius={getRingRadius('inner')}
                startAngle={n}
                endAngle={a[i + 1] + 0.005} // The weird 0.005 allows the gradients to flow into each other a little to stop banding
                fill={`url(#${definitionId.gradientId}-${i})`}
              />
            );
          })}
        </g>
        <path
          id={definitionId.xLargePin}
          d="M2.92971 2.99917C2.96879 1.33152 4.3319 -1.04446e-08 6 -1.44666e-08V-1.44666e-08C7.6681 -1.84885e-08 9.03121 1.33153 9.07029 2.99918L11.8594 122.002C11.9365 125.291 9.29072 128 6 128V128C2.70928 128 0.0634816 125.291 0.140587 122.002L2.92971 2.99917Z"
        />
        <path
          id={definitionId.largePin}
          d="M2.91895 2.99891C2.96406 1.32971 4.33019 0 6 0V0C7.66981 0 9.03594 1.32971 9.08105 2.9989L11.8379 105.002C11.9267 108.288 9.28716 111 6 111V111C2.71284 111 0.0732933 108.288 0.162103 105.002L2.91895 2.99891Z"
        />
        <path
          id={definitionId.mediumPin}
          d="M2.43209 2.49908C2.46989 1.10803 3.60844 0 5 0V0C6.39156 0 7.53011 1.10803 7.56791 2.49908L9.86418 87.0018C9.93859 89.74 7.73918 92 5 92V92C2.26082 92 0.0614128 89.74 0.135819 87.0018L2.43209 2.49908Z"
        />
        <path
          id={definitionId.smallPin}
          d="M1.94523 1.99925C1.97572 0.88635 2.88668 0 4 0V0C5.11332 0 6.02428 0.886347 6.05477 1.99925L7.89045 69.0015C7.95046 71.1919 6.19119 73 4 73V73C1.80881 73 0.0495378 71.1919 0.109548 69.0015L1.94523 1.99925Z"
        />
      </defs>
      <linearGradient id={definitionId.actualBackgroundGradient}>
        <stop offset="0%" stopColor={theme.color.black} stopOpacity="0" />
        <stop offset="100%" stopColor={theme.color.black} stopOpacity="100" />
      </linearGradient>
      <linearGradient id={definitionId.yearMarkerGradient}>
        <stop offset="0%" stopColor={theme.color.brand01} stopOpacity="0.05" />
        <stop offset="100%" stopColor={theme.color.brand01} stopOpacity="0.2" />
      </linearGradient>
      <pattern
        id={definitionId.chartFailPattern}
        patternUnits="userSpaceOnUse"
        width="4"
        height="4"
        patternTransform="rotate(-45 2 2)"
      >
        <path d="M -1,2 l 6,0" stroke={theme.color.bad} strokeWidth="1" />
      </pattern>
      <pattern
        id={definitionId.chartFailPatternInverse}
        patternUnits="userSpaceOnUse"
        width="4"
        height="4"
        patternTransform="rotate(45 2 2)"
      >
        <path d="M -1,2 l 6,0" stroke={theme.color.bad} strokeWidth="1" />
      </pattern>
      <linearGradient
        id={definitionId.noirGradient01}
        x1="0%"
        y1="1"
        x2="0%"
        y2="0"
      >
        <stop offset="0%" stopColor={theme.color.noir} stopOpacity="0.5" />
        <stop offset="100%" stopColor={theme.color.noir} stopOpacity="0.8" />
      </linearGradient>
      <linearGradient id={definitionId.noirGradient02}>
        <stop offset="0%" stopColor={theme.color.noir} stopOpacity="0.5" />
        <stop offset="100%" stopColor={theme.color.noir} stopOpacity="0.8" />
      </linearGradient>
      <linearGradient
        id={definitionId.chart01Gradient01}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.chart01} stopOpacity="0.6" />
        <stop offset="100%" stopColor={theme.color.chart01} stopOpacity="0.2" />
      </linearGradient>
      <linearGradient
        id={definitionId.chart01Gradient02}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.chart01} stopOpacity="0.3" />
        <stop offset="100%" stopColor={theme.color.chart01} stopOpacity="0.1" />
      </linearGradient>
      <linearGradient
        id={definitionId.chart02Gradient01}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.chart02} stopOpacity="0.6" />
        <stop offset="100%" stopColor={theme.color.chart02} stopOpacity="0.2" />
      </linearGradient>
      <linearGradient
        id={definitionId.chart02Gradient02}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.chart02} stopOpacity="0.3" />
        <stop offset="100%" stopColor={theme.color.chart02} stopOpacity="0.1" />
      </linearGradient>
      <linearGradient
        id={definitionId.chart03Gradient01}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.chart03} stopOpacity="0.6" />
        <stop offset="100%" stopColor={theme.color.chart03} stopOpacity="0.2" />
      </linearGradient>
      <linearGradient
        id={definitionId.chart03Gradient02}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.chart03} stopOpacity="0.3" />
        <stop offset="100%" stopColor={theme.color.chart03} stopOpacity="0.1" />
      </linearGradient>
      <linearGradient
        id={definitionId.chart04Gradient01}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.chart04} stopOpacity="0.6" />
        <stop offset="100%" stopColor={theme.color.chart04} stopOpacity="0.2" />
      </linearGradient>
      <linearGradient
        id={definitionId.chart04Gradient02}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.chart04} stopOpacity="0.3" />
        <stop offset="100%" stopColor={theme.color.chart04} stopOpacity="0.1" />
      </linearGradient>
      <linearGradient
        id={definitionId.chart05Gradient01}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.chart05} stopOpacity="0.6" />
        <stop offset="100%" stopColor={theme.color.chart05} stopOpacity="0.2" />
      </linearGradient>
      <linearGradient
        id={definitionId.chart05Gradient02}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.chart05} stopOpacity="0.3" />
        <stop offset="100%" stopColor={theme.color.chart05} stopOpacity="0.1" />
      </linearGradient>
      <linearGradient
        id={definitionId.chart06Gradient01}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.chart06} stopOpacity="0.6" />
        <stop offset="100%" stopColor={theme.color.chart06} stopOpacity="0.2" />
      </linearGradient>
      <linearGradient
        id={definitionId.chart06Gradient02}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.chart06} stopOpacity="0.3" />
        <stop offset="100%" stopColor={theme.color.chart06} stopOpacity="0.1" />
      </linearGradient>
      <linearGradient
        id={definitionId.chartNEGGradient01}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.bad} stopOpacity="0.6" />
        <stop offset="100%" stopColor={theme.color.bad} stopOpacity="0.2" />
      </linearGradient>
      <linearGradient
        id={definitionId.chartNEGGradient02}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.bad} stopOpacity="0.3" />
        <stop offset="100%" stopColor={theme.color.bad} stopOpacity="0.1" />
      </linearGradient>
      <linearGradient
        id={definitionId.chartPOSGradient02}
        x1="0"
        x2="0"
        y1="0"
        y2="1"
      >
        <stop offset="0%" stopColor={theme.color.good} stopOpacity="0.3" />
        <stop offset="100%" stopColor={theme.color.good} stopOpacity="0.1" />
      </linearGradient>
    </svg>
  );
}
